import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useHistory } from "react-router-dom";

import { DetailLayout } from "@/layouts/DetailLayout/DetailLayout";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { notify } from "@/modules/notify/notify";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { TaskItemsHeader } from "@/modules/tasks/components/TaskItemsHeader/TaskItemsHeader";
import { AspectRatio } from "@/modules/theme/components/AspectRatio/AspectRatio";
import { ButtonWithArrow } from "@/modules/theme/components/ButtonWithArrow/ButtonWithArrow";
import { DetailLayoutBox } from "@/modules/theme/components/DetailLayoutBox";
import { ButtonLink } from "@/modules/theme/components/Link/components/ButtonLink/ButtonLink";
import { Link } from "@/modules/theme/components/Link/Link";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { StepStatus } from "@/modules/theme/components/StepStatus/StepStatus";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { AuthenticatedNotFoundPage } from "../404NotFound/AuthenticatedNotFoundPage";

import { useCompleteStepMutation } from "./hooks/useCompleteStepMutation";
import { useGetTutorialProgressDetailsQuery } from "./hooks/useGetTutorialProgressDetailsQuery";
import { useHandleTutorialsNavigation } from "./hooks/useHandleTutorialsNavigation";
import { ReactComponent as DownloadIcon } from "./icons/download-icon.svg";
import { createTutorialDetailsPageStyles } from "./styles/createTutorialDetailsPageStyles";
import { getYouTubeVideoId } from "./utils/getYouTubeVideoId";

const translationNamespace = "pages/TutorialDetailsPage";

export const TutorialDetailsPage = () => {
  const params = authenticatedRouting.useRouteParams("tutorialDetails");

  const classes = createTutorialDetailsPageStyles(useTheme());
  const history = useHistory();
  const { t } = useTranslation(translationNamespace);
  const { userCompanyId } = useAuthContext();
  const {
    data: tutorialProgressDetails,
    isLoading: areTutorialProgressDetailsLoading,
  } = useGetTutorialProgressDetailsQuery(params?.id);
  const handleTutorialsNavigation = useHandleTutorialsNavigation();

  const currentStepArrayIndex = Number(params?.step) - 1;

  const currentStep = tutorialProgressDetails?.steps[currentStepArrayIndex];
  const nextStep = tutorialProgressDetails?.steps[currentStepArrayIndex + 1]
    ? Number(params?.step) + 1
    : null;
  const previousStep = tutorialProgressDetails?.steps[currentStepArrayIndex - 1]
    ? Number(params?.step) - 1
    : null;

  const completeStepMutation = useCompleteStepMutation({
    tutorialId: params?.id,
    nextStep,
    testAvailable: Boolean(tutorialProgressDetails?.testQuestions.length),
  });

  const isStepCompletionPending = completeStepMutation.status === "loading";

  const videoId =
    currentStep?.videoLink && getYouTubeVideoId(currentStep.videoLink);

  const handleStepCompletion = useCallback(() => {
    if (params && tutorialProgressDetails && userCompanyId && currentStep) {
      if (
        tutorialProgressDetails.tutorialStepsCompleted < Number(params.step)
      ) {
        completeStepMutation.mutate({
          companyId: userCompanyId,
          tutorialId: params.id,
          tutorialStepId: currentStep.id,
          tutorialVersion: tutorialProgressDetails.updatedAt,
        });
      } else {
        handleTutorialsNavigation({
          nextStep,
          tutorialId: params.id,
          testAvailable: Boolean(tutorialProgressDetails.testQuestions.length),
        });
      }
    } else {
      notify.alert.error(t("Something went wrong..."));
    }
  }, [
    completeStepMutation,
    handleTutorialsNavigation,
    t,
    nextStep,
    userCompanyId,
    currentStep,
    params,
    tutorialProgressDetails,
  ]);

  if (tutorialProgressDetails && !currentStep) {
    return <AuthenticatedNotFoundPage />;
  }

  return (
    <DetailLayout
      onBackButtonClick={() =>
        history.push(authenticatedRouting.buildUrl("tutorials"))
      }
      variant="tutorial"
      heading={
        areTutorialProgressDetailsLoading ? (
          <Skeleton />
        ) : (
          tutorialProgressDetails?.name
        )
      }
      details={
        areTutorialProgressDetailsLoading ? (
          <Skeleton width={150} />
        ) : (
          tutorialProgressDetails && (
            <>
              <div className={classes.detailsItem}>
                <StepStatus
                  statusIconDarker
                  done={
                    tutorialProgressDetails.tutorialStepsCompleted ===
                    tutorialProgressDetails.steps.length
                  }
                />
                {params?.step}
                <span className={classes.totalStepsNumber}>
                  /{tutorialProgressDetails.steps.length}
                </span>
              </div>
              {tutorialProgressDetails.testQuestions.length ? (
                <div className={classes.detailsItem}>
                  <StepStatus
                    statusIconDarker
                    done={tutorialProgressDetails.testPassed}
                  />
                  {t("Test")}
                </div>
              ) : null}
            </>
          )
        )
      }
    >
      <DetailLayoutBox className={classes.detailLayoutBox}>
        {areTutorialProgressDetailsLoading ? (
          <div className={classes.loaderWrapper}>
            <Loader size={70} variant="primary" />
          </div>
        ) : (
          currentStep && (
            <>
              <TaskItemsHeader>{currentStep.name}</TaskItemsHeader>
              {currentStep.stepAttachments.map(({ fileName, url, id }) => (
                <AspectRatio
                  key={id}
                  ratio={16 / 9}
                  className={classes.aspectRatio}
                  objectFit="contain"
                >
                  <img src={url} alt={fileName} />
                </AspectRatio>
              ))}
              {videoId && (
                <AspectRatio ratio={16 / 9} className={classes.aspectRatio}>
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                    title={t("YouTube video player")}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    className={classes.iframe}
                  />
                </AspectRatio>
              )}
              <div className={classes.contentWrapper}>
                <Typography
                  variant="contentMedium"
                  className={classes.paragraph}
                >
                  {currentStep.description}
                </Typography>
                {currentStep.handbook && (
                  <Link.External
                    to={currentStep.handbook.url}
                    className={classes.handbookLink}
                  >
                    <DownloadIcon />
                    <span>
                      {t("Download handbook")} - {currentStep.handbook.name}
                    </span>
                  </Link.External>
                )}
              </div>
              <div className={classes.buttonsWrapper}>
                <ButtonLink
                  variant="secondary"
                  isDisabled={
                    !(params && previousStep) || isStepCompletionPending
                  }
                  to={
                    params && previousStep
                      ? authenticatedRouting.buildUrl("tutorialDetails", {
                          params: {
                            id: params.id,
                            step: `${previousStep}`,
                          },
                        })
                      : ""
                  }
                  className={classes.button}
                >
                  {t("Back")}
                </ButtonLink>
                <ButtonWithArrow
                  isDisabled={isStepCompletionPending}
                  isLoading={isStepCompletionPending}
                  onClick={() => handleStepCompletion()}
                  className={classes.button}
                  arrowClassName={classes.arrow}
                >
                  {nextStep
                    ? t("Next step")
                    : tutorialProgressDetails.testQuestions.length
                    ? t("Take the test")
                    : t("Finish tutorial")}
                </ButtonWithArrow>
              </div>
            </>
          )
        )}
      </DetailLayoutBox>
    </DetailLayout>
  );
};
